import {
  state,
  style,
  AnimationMetadata,
  transition,
  animate,
  group,
  query,
  sequence,
  keyframes,
  AnimationStateMetadata,
  AnimationStyleMetadata,
  AnimationTransitionMetadata,
  AnimationTriggerMetadata,
} from '@angular/animations';
import { styleVariables } from '../styles/variables';

const notificationOpacityWrapperSelector = '.notification-wrapper';
const notificationContentWrapperSelector = '.notification-content-wrapper';
const dismissableContentSelector = '.notification-center__collapsable-content';

// Base animation timing
const transitionExpandAnimationTiming = `${styleVariables.transitionDurationShort}ms ${styleVariables.transitionCurveStandard}`;

// params definition
const transitionDisplayDefaultConfig = {
  params: {
    notificationWrapperHeight: 0,
    notificationHeaderHeight: 0,
    dismissableContentHeight: 0,
  },
};

/*
These are the 3 states the notification can be in
*/
const displayStateExpanded = state(
  'expanded',
  style({
    height: '{{notificationWrapperHeight}}px',
    opacity: 1,
  }),
  transitionDisplayDefaultConfig
);

const displayStateCollapsed = state(
  'collapsed',
  style({
    height: '{{notificationHeaderHeight}}px',
    opacity: 1,
  }),
  transitionDisplayDefaultConfig
);

const displayStateHidden = state(
  'hidden',
  style({
    height: '0px',
    opacity: 0,
  }),
  transitionDisplayDefaultConfig
);

// The styles of the inner notification content prior to show animation
const notificationContentShowPreAnimationStyles = query(
  notificationOpacityWrapperSelector,
  style({
    opacity: 0,
    transform: 'translateY(-16px)',
  }),
  { optional: true }
);
const notificationContentShowAnimation = query(
  notificationOpacityWrapperSelector,
  animate(
    `150ms 75ms ${styleVariables.transitionCurveSharp}`,
    style({
      opacity: 1,
      transform: 'translateY(0px)',
    })
  ),
  { optional: true }
);

// The styles of the inner notification content prior to hide animation
const notificationContentHidePreAnimationStyles = query(
  notificationContentWrapperSelector,
  style({
    opacity: 1,
    transform: 'translateY(0px)',
  }),
  { optional: true }
);

const notificationContentHideAnimation = query(
  notificationContentWrapperSelector,
  animate(
    `200ms ${styleVariables.transitionCurveStandard}`,
    style({
      opacity: 0,
      transform: 'translateY(-16px)',
    })
  ),
  { optional: true }
);

export const transitionDisplayMetadata = [
  displayStateHidden,
  displayStateCollapsed,
  displayStateExpanded,
  transition(
    'hidden => collapsed',
    [
      sequence([
        notificationContentShowPreAnimationStyles,
        query(dismissableContentSelector, [style({ opacity: 0 })], {
          optional: true,
        }),
        group([
          animate(
            `250ms ${styleVariables.transitionCurveSharp}`,
            keyframes([
              style({ height: '0px', opacity: 0 }),
              style({ height: '{{notificationHeaderHeight}}px', opacity: 1 }),
            ])
          ),
          notificationContentShowAnimation,
        ]),
      ]),
    ],
    transitionDisplayDefaultConfig
  ),
  transition(
    'hidden => expanded',
    [
      sequence([
        notificationContentShowPreAnimationStyles,
        group([
          animate(
            `250ms ${styleVariables.transitionCurveSharp}`,
            keyframes([
              style({ height: '0px', opacity: 0 }),
              style({ height: '{{notificationWrapperHeight}}px', opacity: 1 }),
            ])
          ),
          notificationContentShowAnimation,
        ]),
      ]),
    ],
    transitionDisplayDefaultConfig
  ),
  transition(
    'collapsed => hidden',
    [
      sequence([
        query(dismissableContentSelector, [style({ opacity: 0 })], {
          optional: true,
        }),
        notificationContentHidePreAnimationStyles,
        group([
          notificationContentHideAnimation,
          animate(`150ms 50ms ${styleVariables.transitionCurveStandard}`),
        ]),
      ]),
    ],
    transitionDisplayDefaultConfig
  ),
  transition(
    'expanded => hidden',
    [
      sequence([
        notificationContentHidePreAnimationStyles,
        group([
          notificationContentHideAnimation,
          animate(`150ms 50ms ${styleVariables.transitionCurveStandard}`),
        ]),
      ]),
    ],
    transitionDisplayDefaultConfig
  ),
  transition(
    'collapsed => expanded',
    [
      sequence([
        query(
          dismissableContentSelector,
          [
            style({
              opacity: 0,
              visibility: 'visible',
            }),
          ],
          { optional: true }
        ),

        group([
          animate(
            transitionExpandAnimationTiming,
            keyframes([
              style({ height: '{{notificationHeaderHeight}}px' }),
              style({ height: '{{notificationWrapperHeight}}px' }),
            ])
          ),
          query(
            dismissableContentSelector,
            [animate(transitionExpandAnimationTiming, style({ opacity: 1 }))],
            { optional: true }
          ),
        ]),
      ]),
    ],
    transitionDisplayDefaultConfig
  ),
  transition(
    'expanded => collapsed',
    [
      sequence([
        query(
          dismissableContentSelector,
          [
            style({
              opacity: 1,
            }),
          ],
          { optional: true }
        ),

        group([
          animate(
            transitionExpandAnimationTiming,
            keyframes([
              style({ height: '{{notificationWrapperHeight}}px' }),
              style({ height: '{{notificationHeaderHeight}}px' }),
            ])
          ),
          query(
            dismissableContentSelector,
            [animate(transitionExpandAnimationTiming, style({ opacity: 0 }))],
            { optional: true }
          ),
        ]),
      ]),
    ],
    transitionDisplayDefaultConfig
  ),
];

export const transitionDismissableContentMetadata = [
  state(
    'expanded',
    style({
      opacity: 1,
      visibility: 'visible',
    })
  ),
  state(
    '*',
    style({
      opacity: 0,
      visibility: 'hidden',
    })
  ),
  transition('expanded <=> collapsed', animate('200ms')),
  transition('hidden <=> expanded', animate('200ms')),
  transition('hidden <=> collapsed', animate('200ms')),
];
